/* eslint-disable jsx-a11y/no-onchange */
/* Generated by scripts/genDynamicChecklistReadOnly.mjs — edit script and re-run to sync from RaiseComplaintChecklist.js */
import React, { useEffect, useMemo, useRef, useState } from "react";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import LabelIcon from "@mui/icons-material/Label";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DescriptionIcon from "@mui/icons-material/Description";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import SubjectIcon from "@mui/icons-material/Subject";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NumbersIcon from "@mui/icons-material/Numbers";
import ImageIcon from "@mui/icons-material/Image";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { checklistQuestionAPI, getApiBaseUrl } from "../services/api";
import UploadedFileList from "./UploadedFileList";
import {
  filterGeneralQuestionTree,
  filterQuestionTreeBySrCategoryExcludingGeneral,
} from "../utils/checklistSrCategoryUtils";

// Order matches DB: DisplayOrder, then CreatedOn, then questionCode.
export function buildChecklistTree(flatQuestions) {
    if (!flatQuestions || flatQuestions.length === 0) return [];
    const flat = Array.isArray(flatQuestions) ? flatQuestions : [];
    const map = new Map();
    flat.forEach((q, idx) => {
      const code = q.questionCode ?? q.QuestionCode;
      if (code == null || String(code).trim() === "") return;
      const node = {
        ...q,
        questionCode: q.questionCode ?? q.QuestionCode,
        parentQuestionCode: q.parentQuestionCode ?? q.ParentQuestionCode,
        questionText: q.questionText ?? q.QuestionText,
        questionAnsText: q.questionAnsText ?? q.QuestionAnsText,
        questionTypeCode: q.questionTypeCode ?? q.QuestionTypeCode,
        displayOrder: q.displayOrder ?? q.DisplayOrder ?? 0,
        createdOn: q.createdOn ?? q.CreatedOn,
        children: [],
        _flatIndex: idx,
      };
      map.set(String(code).trim(), node);
    });
    const roots = [];
    flat.forEach((q) => {
      const code = q.questionCode ?? q.QuestionCode;
      const node = code != null && String(code).trim() !== "" ? map.get(String(code).trim()) : null;
      if (!node) return;
      const parentCode = node.parentQuestionCode;
      const parentKey = parentCode != null && String(parentCode).trim() !== "" ? String(parentCode).trim() : null;
      if (parentKey && map.has(parentKey)) map.get(parentKey).children.push(node);
      else roots.push(node);
    });
    const sortByOrder = (a, b) => {
      const orderA = Number(a.displayOrder) ?? 0;
      const orderB = Number(b.displayOrder) ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
      const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
      if (dateA !== dateB) return dateA - dateB;
      const idxA = a._flatIndex ?? 999999;
      const idxB = b._flatIndex ?? 999999;
      if (idxA !== idxB) return idxA - idxB;
      const codeA = a.questionCode ?? "";
      const codeB = b.questionCode ?? "";
      return String(codeA).localeCompare(String(codeB));
    };
    const sortChildrenRecursive = (node) => {
      if (node.children && node.children.length) {
        node.children.sort(sortByOrder);
        node.children.forEach(sortChildrenRecursive);
      }
    };
    roots.sort(sortByOrder);
    roots.forEach(sortChildrenRecursive);
    return roots;
}

function colClassFlat(n) {
  if (n <= 1) return "col-12 col-md-12 dynamic-q-row dynamic-q-product-details";
  if (n === 2) return "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details";
  return "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details";
}


function ChecklistFlatFallback({ questions }) {
  const n = questions.length;
  return (
    <div className="raise-page">
      <div className="checklist-form raise-complaint-form dynamic-checklist-wrapper pt-0">
        <div className="dynamic-q-section-children">
          <div className="row g-3">
            {questions.map((q) => {
              const isSelectType =
                q.isBoolean ||
                q.response === "Yes" ||
                q.response === "No" ||
                (q.response && String(q.response).length <= 30 && !/\d/.test(String(q.response)));
              return (
                <div key={q.questionCode || q.questionText} className={colClassFlat(n)}>
                  <span className="dynamic-q-icon" aria-hidden="true">
                    {isSelectType ? <ArrowDropDownIcon sx={{ fontSize: 20 }} /> : <SubjectIcon sx={{ fontSize: 20 }} />}
                  </span>
                  <div className="dynamic-q-control">
                    <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                      {q.questionText || q.questionCode}
                    </label>
                    <input
                      type="text"
                      className="form-control checklist-form-control component-details-input"
                      value={q.response || ""}
                      readOnly
                      placeholder="—"
                      style={{ backgroundColor: "#fff", color: "#333" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function createChecklistRenderers(answers, collapsedSections, setCollapsedSections) {
  const iconSize = { fontSize: 20 };
  const setDynamicAnswer = () => {};
  const errorsForTemplate = {};
  const apiBaseUrl = getApiBaseUrl();

  const toFullUrl = (pathOrUrl) => {
    const v = pathOrUrl == null ? "" : String(pathOrUrl).trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    const clean = v.replace(/^\/+/, "");
    return clean ? `${apiBaseUrl}/${clean}` : "";
  };

  const parseQt009AnswerPaths = (raw) => {
    if (Array.isArray(raw)) {
      return raw.map((x) => String(x || "").trim()).filter(Boolean);
    }
    if (raw == null) return [];
    const s = String(raw).trim();
    if (!s) return [];
    if (s.startsWith("[") && s.endsWith("]")) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parsed.map((x) => String(x || "").trim()).filter(Boolean);
        }
      } catch {
        // fall through to plain string handling
      }
    }
    return [s];
  };

  const answerForCode = (questionCode) => {
    const k = questionCode == null ? "" : String(questionCode).trim();
    if (!k) return undefined;
    if (answers && Object.prototype.hasOwnProperty.call(answers, k)) return answers[k];
    const lower = k.toLowerCase();
    const keys = answers && typeof answers === "object" ? Object.keys(answers) : [];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (String(key).trim().toLowerCase() === lower) return answers[key];
    }
    return undefined;
  };

  const normalizeOptionText = (value) =>
    String(value ?? "")
      .replace(/\*/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const conditionalMatchesSelected = (conditionalValue, selectedTexts) => {
    const raw = String(conditionalValue ?? "").trim();
    if (!raw) return false;
    const parts = raw
      .split(/[|,]/)
      .map((p) => normalizeOptionText(p))
      .filter(Boolean);
    if (parts.length === 0) return false;
    return parts.some((part) => selectedTexts.has(part));
  };

  const parseQuestionAnsTextOptions = (node) => {
    const ansText = String(node?.questionAnsText ?? node?.QuestionAnsText ?? "").trim();
    if (!ansText) return [];
    return ansText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .map((text) => ({ questionText: text }));
  };

  const getSelectableOptionItems = (node) => {
    const ch = Array.isArray(node?.children) ? node.children : [];
    const filtered = ch.filter((child) => {
      const conditionalValue = String(child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? "").trim();
      const text = String(child?.questionText || child?.QuestionText || "").trim();
      if (conditionalValue) return false;
      return Boolean(text);
    });
    if (filtered.length) return filtered;
    const fromAns = parseQuestionAnsTextOptions(node);
    if (fromAns.length && ch.some(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim()
    )) {
      return fromAns;
    }
    return ch;
  };

  const getQt001Qt003OptionItems = (node) => {
    const selectable = getSelectableOptionItems(node);
    if (selectable.length) return selectable;
    const fromAns = parseQuestionAnsTextOptions(node);
    if (fromAns.length) return fromAns;
    return Array.isArray(node?.children) ? node.children : [];
  };

  const getSelectedOptionTextsForNode = (node) => {
    const tc = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    const qc = node?.questionCode || node?.QuestionCode;
    const val = qc ? answerForCode(qc) : null;

    if (tc === "QT003") {
      if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean);
      if (typeof val === "string") {
        const s = val.trim();
        if (!s) return [];
        if (s.startsWith("[") && s.endsWith("]")) {
          try {
            const parsed = JSON.parse(s);
            if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim()).filter(Boolean);
          } catch (_) {
            /* ignore malformed draft string */
          }
        }
        return [s];
      }
      return val != null ? [String(val).trim()] : [];
    }

    if (val == null) return [];
    const single = String(val).trim();
    return single ? [single] : [];
  };

  const getVisibleConditionalTextChildren = (node) => {
    const parentType = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (parentType !== "QT001" && parentType !== "QT003") return [];

    const ch = Array.isArray(node?.children) ? node.children : [];
    const selectedTexts = new Set(
      getSelectedOptionTextsForNode(node).map((t) => normalizeOptionText(t))
    );
    if (selectedTexts.size === 0) return [];

    const optionTexts = new Set(
      getQt001Qt003OptionItems(node)
        .map((opt) => normalizeOptionText(opt?.questionText || opt?.QuestionText || ""))
        .filter(Boolean)
    );

    const byKey = new Map();
    const addDirectChild = (child) => {
      if (!child) return;
      const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
      const conditionalValue = String(
        child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? ""
      ).trim();
      const branchKids = Array.isArray(child?.children) ? child.children : [];
      if (childText && optionTexts.has(childText) && !conditionalValue && branchKids.length === 0) {
        return;
      }
      const t = String(child.questionTypeCode || child.QuestionTypeCode || "").toUpperCase();
      const key =
        String(child.questionCode || child.QuestionCode || "").trim() ||
        `${String(child.questionText || child.QuestionText || "").trim()}__${t}`;
      if (!byKey.has(key)) byKey.set(key, child);
    };

    ch.forEach((optionNode) => {
      const optionText = normalizeOptionText(
        optionNode?.questionText || optionNode?.QuestionText || ""
      );
      if (!optionText || !selectedTexts.has(optionText)) return;
      addDirectChild(optionNode);
    });

    ch.forEach((child) => {
      const conditionalValue = child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? "";
      if (!conditionalMatchesSelected(conditionalValue, selectedTexts)) return;
      addDirectChild(child);
    });

    ch.forEach((child) => {
      const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
      if (childText && selectedTexts.has(childText) && !optionTexts.has(childText)) {
        addDirectChild(child);
      }
    });

    return Array.from(byKey.values());
  };

  const OPTION_ROW_LABEL_ONLY_TYPE_CODES = new Set(["QT001", "QT002", "QT003"]);

  const isChecklistOptionBasedParentType = (typeCode) => {
    const t = String(typeCode || "").toUpperCase();
    return t === "QT001" || t === "QT003" || t === "QT011" || t === "QT012";
  };

  const normalizeChecklistOptionParentNode = (node) => {
    const t = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (t === "QT011") return { ...node, questionTypeCode: "QT003", QuestionTypeCode: "QT003" };
    if (t === "QT012") return { ...node, questionTypeCode: "QT001", QuestionTypeCode: "QT001" };
    return node;
  };

  const isOptionRowTypedFollowUpChild = (child) => {
    if (!child) return false;
    const branchKids = Array.isArray(child?.children) ? child.children : [];
    if (branchKids.length > 0) return false;
    const conditionalValue = String(
      child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? ""
    ).trim();
    if (conditionalValue) return false;
    const t = String(child?.questionTypeCode || child?.QuestionTypeCode || "").toUpperCase();
    if (!t || OPTION_ROW_LABEL_ONLY_TYPE_CODES.has(t)) return false;
    return true;
  };

  const getVisibleOptionRowTypedFollowUps = (node) => {
    const parentType = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (!isChecklistOptionBasedParentType(parentType)) return [];

    const normalizedParent = normalizeChecklistOptionParentNode(node);
    const children = Array.isArray(node?.children) ? node.children : [];
    const selectedTexts = new Set(
      getSelectedOptionTextsForNode(normalizedParent).map((text) => normalizeOptionText(text))
    );
    if (selectedTexts.size === 0) return [];

    const optionTexts = new Set(
      getQt001Qt003OptionItems(normalizedParent)
        .map((opt) => normalizeOptionText(opt?.questionText || opt?.QuestionText || ""))
        .filter(Boolean)
    );

    const byKey = new Map();
    children.forEach((child) => {
      const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
      if (!childText || !selectedTexts.has(childText) || !optionTexts.has(childText)) return;
      if (!isOptionRowTypedFollowUpChild(child)) return;
      const t = String(child?.questionTypeCode || child?.QuestionTypeCode || "").toUpperCase();
      const key =
        String(child?.questionCode || child?.QuestionCode || "").trim() ||
        `${childText}__${t}`;
      if (!byKey.has(key)) byKey.set(key, child);
    });
    return Array.from(byKey.values());
  };

  const mergeVisibleChecklistFollowUpChildren = (node) => {
    const normalizedParent = normalizeChecklistOptionParentNode(node);
    const byKey = new Map();
    const addChild = (child) => {
      if (!child) return;
      const t = String(child?.questionTypeCode || child?.QuestionTypeCode || "").toUpperCase();
      const key =
        String(child?.questionCode || child?.QuestionCode || "").trim() ||
        `${String(child?.questionText || child?.QuestionText || "").trim()}__${t}`;
      if (!byKey.has(key)) byKey.set(key, child);
    };
    getVisibleConditionalTextChildren(normalizedParent).forEach(addChild);
    getVisibleOptionRowTypedFollowUps(node).forEach(addChild);
    return Array.from(byKey.values());
  };

  const buildOptionRowFollowUpRenderOptions = (child, parentNode, baseOptions = {}) => {
    const parentType = String(
      parentNode?.questionTypeCode || parentNode?.QuestionTypeCode || ""
    ).toUpperCase();
    if (!isChecklistOptionBasedParentType(parentType)) return baseOptions;
    const normalizedParent = normalizeChecklistOptionParentNode(parentNode);
    const optionTexts = new Set(
      getQt001Qt003OptionItems(normalizedParent)
        .map((opt) => normalizeOptionText(opt?.questionText || opt?.QuestionText || ""))
        .filter(Boolean)
    );
    const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
    if (!childText || !optionTexts.has(childText) || !isOptionRowTypedFollowUpChild(child)) {
      return baseOptions;
    }
    return {
      ...baseOptions,
      optionRowTypedFollowUp: true,
      parentOptionText: child?.questionText || child?.QuestionText || "",
    };
  };

  const getChecklistLevelInputText = (questionNode) =>
    String(questionNode?.questionAnsText ?? questionNode?.QuestionAnsText ?? "").trim();

  const renderChecklistLevelInputLabel = (levelText, {
    typeIcon,
    labelClass,
    labelStyle,
    mandatory,
    indentStyle,
  }) => {
    if (!levelText) return null;
    return (
      <div
        className="dynamic-q-option-item dynamic-q-row dynamic-q-product-details"
        style={indentStyle}
      >
        <span className="dynamic-q-icon" aria-hidden="true">
          {typeIcon}
        </span>
        <div className="dynamic-q-control dynamic-q-control-inline-auto">
          <label className={`${labelClass} dynamic-q-label-qt001-qt003`} style={labelStyle}>
            {levelText}
            {mandatory && <span className="text-danger ms-1">*</span>}
          </label>
        </div>
      </div>
    );
  };

  const getDynamicQuestionTypeIcon = (typeCode) => {
    const code = (typeCode || "").toUpperCase();
    switch (code) {
      case "QT001":
        return <RadioButtonCheckedIcon sx={iconSize} aria-hidden="true" />;
      case "QT002":
        return <LabelIcon sx={iconSize} aria-hidden="true" />;
      case "QT003":
        return <CheckBoxIcon sx={iconSize} aria-hidden="true" />;
      case "QT004":
        return <ArrowDropDownIcon sx={iconSize} aria-hidden="true" />;
      case "QT005":
        return <DescriptionIcon sx={iconSize} aria-hidden="true" />;
      case "QT006":
        return <ChecklistRtlIcon sx={iconSize} aria-hidden="true" />;
      case "QT007":
        return <SubjectIcon sx={iconSize} aria-hidden="true" />;
      case "QT008":
        return <NumbersIcon sx={iconSize} aria-hidden="true" />;
      case "QT009":
        return <ImageIcon sx={iconSize} aria-hidden="true" />;
      case "QT010":
        return <CalendarMonthIcon sx={iconSize} aria-hidden="true" />;
      default:
        return <EditNoteIcon sx={iconSize} aria-hidden="true" />;
    }
  };

  // Render one question from the dynamic checklist (by QuestionTypeCode). level = indent depth for n-level hierarchy.
  // options: { …, moreThanTwoSectionDirectChildren, qt004HalfWidthBetweenSectionSiblings, … }.
  // moreThanTwoSectionDirectChildren: QT006 section has >2 direct children → QT002/QT005/QT007 (not QT001/QT003) use col-lg-3 in row; radio/checkbox count rules unchanged for QT001/QT003.
  const renderDynamicQuestion = (node, level = 0, options = {}) => {
    const code = node.questionCode || node.QuestionCode;
    const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
    const rawText = node.questionText || node.QuestionText || "";
    const text = rawText;
    const isOthersPleaseSpecifyQuestion =
      normalizeOptionText(rawText) === normalizeOptionText("Others (Please specify)");
    const children = node.children || [];
    const mandatoryRaw = node.isMandatory ?? node.IsMandatory;
    const mandatory =
      mandatoryRaw !== null &&
      mandatoryRaw !== undefined &&
      mandatoryRaw !== false &&
      mandatoryRaw !== 0 &&
      mandatoryRaw !== "0";
    const value = answerForCode(code);
    const name = `q_${code}`;
    const typeIcon = getDynamicQuestionTypeIcon(typeCode);
    const indentStyle = options.flatQt001Qt003ConditionalChild
      ? undefined
      : level > 0
        ? { marginLeft: level * 28, paddingLeft: 12, borderLeft: "3px solid #e0e0e0" }
        : undefined;

    const labelClass = `form-label checklist-form-label component-details-label ${mandatory ? "required" : ""}`;
    const labelStyle = { textAlign: "left", fontWeight: "bold", fontSize: "14px" };

    // QT013 (Level) – transparent wrapper; render nested questions without an extra field.
    if (typeCode === "QT013") {
      const levelChildren = children || [];
      if (levelChildren.length === 0) return null;
      return (
        <React.Fragment key={code || `level_${level}`}>
          {levelChildren.map((child) => renderDynamicQuestion(child, level, options))}
        </React.Fragment>
      );
    }

    // QT014 (Level Image Upload) – hide QuestionText; level text label like QT001, then upload.
    if (typeCode === "QT014") {
      const levelText = getChecklistLevelInputText(node);
      return (
        <React.Fragment key={code || `level_image_${level}`}>
          {renderChecklistLevelInputLabel(levelText, {
            typeIcon,
            labelClass,
            labelStyle,
            mandatory,
            indentStyle,
          })}
          {renderDynamicQuestion(
            { ...node, questionTypeCode: "QT009", QuestionTypeCode: "QT009" },
            level,
            { ...options, suppressQtFieldLabel: true }
          )}
        </React.Fragment>
      );
    }

    // QT015 (Level Text Box) – hide QuestionText; level text label like QT001, then text box.
    if (typeCode === "QT015") {
      const levelText = getChecklistLevelInputText(node);
      return (
        <React.Fragment key={code || `level_text_${level}`}>
          {renderChecklistLevelInputLabel(levelText, {
            typeIcon,
            labelClass,
            labelStyle,
            mandatory,
            indentStyle,
          })}
          {renderDynamicQuestion(
            { ...node, questionTypeCode: "QT007", QuestionTypeCode: "QT007" },
            level,
            { ...options, suppressQtFieldLabel: true }
          )}
        </React.Fragment>
      );
    }

    // Group Radio Button (QT012) – same runtime behavior as QT001.
    if (typeCode === "QT012") {
      return renderDynamicQuestion(
        { ...node, questionTypeCode: "QT001", QuestionTypeCode: "QT001" },
        level,
        options
      );
    }

    // Group Check Box (QT011) – same runtime behavior as QT003.
    if (typeCode === "QT011") {
      return renderDynamicQuestion(
        { ...node, questionTypeCode: "QT003", QuestionTypeCode: "QT003" },
        level,
        options
      );
    }

    // Label only (QT002) – Product Details style
    // When parent has children or node is a child (level > 0), use col-md-6 so parent/child sit side by side
    if (typeCode === "QT002") {
      const hasChildrenOrIsChild = children.length > 0 || level > 0;
      const qt002ColClass =
        options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
            ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
            : "col-12 col-md-12 dynamic-q-row dynamic-q-product-details";
      return (
        <React.Fragment key={code}>
          <div className={qt002ColClass} style={indentStyle}>
            <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
            <label className={labelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
          </div>
          {children.length > 0 && (
            <div className="col-12 dynamic-q-qt002-children">
              <div className="row g-3">
                {children.map((child) =>
                  renderDynamicQuestion(child, level + 1, options)
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      );
    }

    // QT006 as section header when it has sub-questions (children that have their own children)
    // col-md-6 so it sits next to "Does Prelam has printed information..." in the same row
    const hasSubQuestionChildren = (node.children || []).some((c) => (c.children || []).length > 0);
    if (typeCode === "QT006" && hasSubQuestionChildren) {
      return (
        <div key={code} className="col-12 col-md-12 dynamic-q-row dynamic-q-product-details dynamic-q-section" style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <label className={labelClass} style={labelStyle}>
            {text}
            {mandatory && <span className="text-danger ms-1">*</span>}
          </label>
        </div>
      );
    }

    // QT006 (no sub-question children): either Multiple Choice (option rows from admin, often Label) → dropdown on parent, or QT007-only block → textboxes per child.
    if (typeCode === "QT006" && !hasSubQuestionChildren) {
      const qt007Children = (children || []).filter(
        (c) => (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase() === "QT007"
      );
      const qt005Children = (children || []).filter(
        (c) => (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase() === "QT005"
      );
      const allChildrenAreQt007 =
        (children || []).length > 0 && qt007Children.length === (children || []).length;

      // Make all QT006 blocks collapsible like QT006 sections (e.g. "Product Storage")
      const isCollapsed = collapsedSections.has(code);
      const shouldBeExpanded = !isCollapsed;
      const toggleSection = (e) => {
        e.stopPropagation();
        setCollapsedSections((prev) => {
          const next = new Set(prev);
          if (next.has(code)) next.delete(code);
          else next.add(code);
          return next;
        });
      };

      // QT005 / QT007 leaf children under QT006 — render each field (mixed QT005+QT007 when both exist).
      if (qt005Children.length > 0) {
        const fieldsToRender =
          qt007Children.length > 0
            ? (children || []).filter((c) => {
                const t = (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase();
                return t === "QT005" || t === "QT007";
              })
            : qt005Children;
        return (
          <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
            <div
              className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
              onClick={toggleSection}
              style={{ cursor: "pointer" }}
            >
              <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
                {text}
                {mandatory && <span className="text-danger ms-1">*</span>}
              </label>
              <span className="dynamic-q-section-caret">
                {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </span>
            </div>
            {shouldBeExpanded && (
              <div className="dynamic-q-section-children">
                <div className="row g-3">
                  {fieldsToRender.map((child, idx) =>
                    renderDynamicQuestion(
                      child,
                      level + 1,
                      {
                        parentIsQt006: true,
                        siblingIndex: idx,
                        inDynamicQSectionChildren: true,
                        ...(fieldsToRender.length === 1
                          ? { soleQt005OrQt007InSectionChildren: true }
                          : {}),
                        ...(fieldsToRender.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                        ...(fieldsToRender.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                      }
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }

      // Admin "Multiple Choice" is QT006 with leaf options (e.g. QT002 Label). Mirror QT004: one select, value on parent questionCode.
      if (!allChildrenAreQt007 && (children || []).length > 0) {
        const leafOptions = (children || []).filter((c) => !((c.children || []).length > 0));
        const mcOptions = leafOptions.length ? leafOptions : children;
        const optionLikeLeafTypeCodes = new Set(["QT001", "QT002", "QT003", "QT004", "QT006"]);
        const hasOnlyOptionLikeLeafChildren =
          mcOptions.length > 0 &&
          mcOptions.every((child) =>
            optionLikeLeafTypeCodes.has(
              String(child.questionTypeCode || child.QuestionTypeCode || "").toUpperCase()
            )
          );

        if (!hasOnlyOptionLikeLeafChildren) {
          return (
            <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
              <div
                className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
                onClick={toggleSection}
                style={{ cursor: "pointer" }}
              >
                <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
                  {text}
                  {mandatory && <span className="text-danger ms-1">*</span>}
                </label>
                <span className="dynamic-q-section-caret">
                  {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
              </div>
              {shouldBeExpanded && (
                <div className="dynamic-q-section-children">
                  <div className="row g-3">
                    {mcOptions.map((child, idx) =>
                      renderDynamicQuestion(
                        child,
                        level + 1,
                        {
                          parentIsQt006: true,
                          siblingIndex: idx,
                          inDynamicQSectionChildren: true,
                          ...(mcOptions.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                          ...(mcOptions.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                        }
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }

        return (
          <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
            <div
              className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
              onClick={toggleSection}
              style={{ cursor: "pointer" }}
            >
              <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
                {text}
                {mandatory && <span className="text-danger ms-1">*</span>}
              </label>
              <span className="dynamic-q-section-caret">
                {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </span>
            </div>
            {shouldBeExpanded && (
              <div className="dynamic-q-section-children">
                <div className="row g-3">
                  <div className="col-12 col-md-3 dynamic-q-row dynamic-q-product-details">
                    <div className="dynamic-q-control">
                      <select
                        disabled
                        className="form-select form-control checklist-form-control component-details-input"
                        value={value || ""}
                      >
                        <option value="">Select</option>
                        {mcOptions.map((opt, idx) => {
                          const optCode = opt.questionCode || opt.QuestionCode || idx;
                          const optText = opt.questionText || opt.QuestionText || "";
                          return (
                            <option key={optCode} value={optText}>
                              {optText}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      return (
        <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
          <div
            className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
            onClick={toggleSection}
            style={{ cursor: "pointer" }}
          >
            <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <span className="dynamic-q-section-caret">
              {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </span>
          </div>
          {shouldBeExpanded && (
            <div className="dynamic-q-section-children">
              <div className="row g-3">
                <div className="col-12 col-md-12 dynamic-q-row dynamic-q-product-details dynamic-q-qt006-with-inputs">
                  <div className="dynamic-q-control">
                    {qt007Children.length > 0 && (
                      <div className="dynamic-q-qt006-qt007-inputs mt-2 d-flex flex-wrap">
                        {qt007Children.map((child) => {
                          const childCode = child.questionCode || child.QuestionCode;
                          const childText = child.questionText || child.QuestionText || "";
                          const childValue = childCode ? (answerForCode(childCode) ?? "") : "";
                          const childMandatoryRaw = child.isMandatory ?? child.IsMandatory;
                          const childMandatory =
                            childMandatoryRaw !== null &&
                            childMandatoryRaw !== undefined &&
                            childMandatoryRaw !== false &&
                            childMandatoryRaw !== 0 &&
                            childMandatoryRaw !== "0";
                          const childLabelClass = `form-label checklist-form-label component-details-label ${childMandatory ? "required" : ""}`;
                          const childHasError = false;
                          const childErrorMessage = "";
                          const handleChildQt007Change = () => {};
                          return (
                            <div key={childCode || childText} className="col-12 col-md-6 col-lg-3 mb-2 px-2">
                              <label className={childLabelClass} style={{ textAlign: "left", fontWeight: "bold", fontSize: "14px" }}>
                                {childText}
                                {childMandatory && <span className="text-danger ms-1">*</span>}
                              </label>
                              <input
                                type="text"
                                className={`form-control checklist-form-control component-details-input mt-1 ${childHasError ? "is-invalid" : ""}`}
                                placeholder="Enter text"
                                value={childValue}
                                readOnly
                              />
                              
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Radio Button (QT001) – label above, options in styled block
    if (typeCode === "QT001") {
      const optionItems = getQt001Qt003OptionItems(node);
      const visibleConditionalChildren = mergeVisibleChecklistFollowUpChildren(node);
      const qt001ColClass = "dynamic-q-option-item dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={`${qt001ColClass} dynamic-q-row dynamic-q-product-details`} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control dynamic-q-control-inline-auto">
            <label className={`${labelClass} dynamic-q-label-qt001-qt003`} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <div className="component-details-options component-details-input dynamic-q-inline-options">
              <div className="radio-group d-flex flex-wrap gap-3 flex-row">
                {optionItems.map((opt, idx) => {
                  const optText = opt.questionText || opt.QuestionText || "";
                  const id = `${name}_${idx}`;
                  const valStr = String(value ?? "").trim();
                  const optStr = String(optText ?? "").trim();
                  const isChecked = valStr === optStr || valStr.toLowerCase() === optStr.toLowerCase();
                  return (
                    <div key={id} className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name={name}
                        id={id}
                        value={optText}
                        checked={isChecked}
                        disabled
                        onChange={(e) => e.preventDefault()}
                      />
                      <label className="form-check-label" htmlFor={id}>{optText}</label>
                    </div>
                  );
                })}
              </div>
            </div>
            {visibleConditionalChildren.length > 0 && (
              <div className="row g-3 mt-1 dynamic-q-conditional-children-row">
                {visibleConditionalChildren.map((child) =>
                  renderDynamicQuestion(
                    child,
                    level,
                    buildOptionRowFollowUpRenderOptions(child, node, {
                      ...options,
                      flatQt001Qt003ConditionalChild: true,
                      parentIsQt006: false,
                      siblingIndex: undefined,
                      ...(visibleConditionalChildren.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                      ...(visibleConditionalChildren.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                    })
                  )
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Check Box (QT003) – label above, checkboxes in styled block
    // Show ALL children as options (e.g. Universal, Studio, Zenith). Children with sub-questions
    // (e.g. Universal) also get a separate row below with their dropdown/sub-options.
    if (typeCode === "QT003") {
      const optionItems = getQt001Qt003OptionItems(node);
      const visibleConditionalChildren = mergeVisibleChecklistFollowUpChildren(node);
      const hasOthersWithTwoMoreOptions =
        optionItems.length >= 3 &&
        optionItems.some((opt) => {
          const optText = normalizeOptionText(opt?.questionText || opt?.QuestionText || "");
          return optText === normalizeOptionText("Others (Please specify)");
        });
      const selected = (() => {
        if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
        if (typeof value === "string") {
          const s = value.trim();
          if (s.startsWith("[") && s.endsWith("]")) {
            try {
              const parsed = JSON.parse(s);
              if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim()).filter(Boolean);
            } catch (_) {}
          }
          return s ? [s] : [];
        }
        return value ? [String(value).trim()] : [];
      })();
      const hasOthersSelectedInCheckbox = selected.some(
        (s) => normalizeOptionText(s) === normalizeOptionText("Others (Please specify)")
      );
      const qt003ColClass =
        hasOthersWithTwoMoreOptions
          ? "dynamic-q-option-item-full dynamic-q-row dynamic-q-product-details"
          : "dynamic-q-option-item dynamic-q-row dynamic-q-product-details";
      const stackCheckboxOptionsBelowLabel = optionItems.length > 4;
      return (
        <div key={code} className={`${qt003ColClass} dynamic-q-row dynamic-q-product-details`} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div
            className={`dynamic-q-control dynamic-q-control-inline-auto${
              stackCheckboxOptionsBelowLabel ? " dynamic-q-control-inline-break" : ""
            }`}
          >
            <label className={`${labelClass} dynamic-q-label-qt001-qt003`} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <div className="component-details-options component-details-input dynamic-q-checkbox-options dynamic-q-inline-options">
              <div className="d-flex flex-wrap gap-3">
                {optionItems.map((opt, idx) => {
                  const optCode = opt.questionCode || opt.QuestionCode || `opt_${idx}`;
                  const optText = opt.questionText || opt.QuestionText || "";
                  const id = `${name}_${String(optCode).replace(/\s/g, "_")}`;
                  return (
                    <div key={optCode} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={id}
                        checked={selected.includes(optText)}
                        disabled
                        onChange={(e) => e.preventDefault()}
                      />
                      <label className="form-check-label" htmlFor={id}>{optText}</label>
                    </div>
                  );
                })}
              </div>
            </div>
            {visibleConditionalChildren.length > 0 && (
              <div
                className="row g-3 mt-1 dynamic-q-conditional-children-row"
                style={{
                  width: "100%",
                  minWidth: "100%",
                  maxWidth: "100%",
                  flexBasis: "100%",
                }}
              >
                {visibleConditionalChildren.map((child) =>
                  renderDynamicQuestion(
                    child,
                    level,
                    buildOptionRowFollowUpRenderOptions(child, node, {
                      ...options,
                      flatQt001Qt003ConditionalChild: true,
                      parentIsQt006: false,
                      siblingIndex: undefined,
                      ...(
                        hasOthersSelectedInCheckbox &&
                        normalizeOptionText(child?.questionText || child?.QuestionText || "") ===
                          normalizeOptionText("Others (Please specify)")
                          ? { forceFullWidthFromConditionalCheckbox: true }
                          : {}
                      ),
                      ...(
                        hasOthersSelectedInCheckbox &&
                        normalizeOptionText(child?.questionText || child?.QuestionText || "") ===
                          normalizeOptionText("Others (Please specify)")
                          ? { forceMandatoryWhenOthersSelected: true }
                          : {}
                      ),
                      ...(hasOthersWithTwoMoreOptions ? { forceFullWidthFromConditionalCheckbox: true } : {}),
                      ...(visibleConditionalChildren.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                      ...(visibleConditionalChildren.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                    })
                  )
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Dropdowns (QT004) – label above, select with Product Details styling
    // Options = all children (e.g. under "Universal" the dropdown shows Universal-01, Universal-02)
    if (typeCode === "QT004") {
      const qt004DropdownItems = (children || []).length ? (children || []) : [];
      const qt004ColClass =
        options.qt004HalfWidthBetweenSectionSiblings
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.lastDropdownFullWidthAfterNestedSection
            ? "col-12 col-md-6 col-lg-12 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
            ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
            : "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt004ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
            <label className={labelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <select
              disabled
              className="form-select form-control checklist-form-control component-details-input"
              value={value || ""}
            >
              <option value="">Select</option>
              {qt004DropdownItems.map((opt, idx) => {
                const optCode = opt.questionCode || opt.QuestionCode || idx;
                const optText = opt.questionText || opt.QuestionText || "";
                return <option key={optCode} value={optText}>{optText}</option>;
              })}
            </select>
          </div>
        </div>
      );
    }

    // Special Text (QT005) – always full row width (long descriptive answers)
    if (typeCode === "QT005") {
      const mandatoryForRender = mandatory || Boolean(options.forceMandatoryWhenOthersSelected);
      const qt005ColClass =
        "col-12 col-md-12 col-lg-12 dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt005ColClass} style={indentStyle}>
          {!isOthersPleaseSpecifyQuestion && (
            <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          )}
          <div className="dynamic-q-control">
            <label
              className={`${labelClass} dynamic-q-label-qt005`}
              style={labelStyle}
            >
              {text}
              {mandatoryForRender && <span className="text-danger ms-1">*</span>}
            </label>
            <textarea
              readOnly
              className="form-control checklist-form-control component-details-input dynamic-q-input-textbox"
              rows={1}
              placeholder="Enter text"
              value={value || ""}
              title={value || ""}
              style={{ minHeight: "47px", maxHeight: "47px", height: "47px", resize: "none" }}
            />
          </div>
        </div>
      );
    }

    // Text Box (QT007) – same size and validation as Search via Invoice No.: single-line input; only A–Z, a–z, 0–9; no spaces/special chars; show "Special characters are not allowed." when invalid.
    if (typeCode === "QT007") {
      const mandatoryForRender = mandatory || Boolean(options.forceMandatoryWhenOthersSelected);
      const hasError = false;
      const errorMessage = "";
      const handleQt007Change = () => {};
      const qt007FullWidthInSection =
        options.soleQt005OrQt007InSectionChildren ||
        options.lastTextFieldFullWidthAfterNestedSection;
      const qt007ColClass =
        options.forceFullWidthFromConditionalCheckbox
          ? "col-12 col-md-12 col-lg-12 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
            ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
            : options.inDynamicQSectionChildren && qt007FullWidthInSection
              ? "col-12 col-md-6 col-lg-12 dynamic-q-row dynamic-q-product-details"
              : options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
                ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
                : "col-12 col-md-6 dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt007ColClass} style={indentStyle}>
          {!isOthersPleaseSpecifyQuestion && (
            <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          )}
          <div className="dynamic-q-control">
            {!options.suppressQtFieldLabel && (
              <label className={labelClass} style={labelStyle}>
                {text}
                {mandatoryForRender && <span className="text-danger ms-1">*</span>}
              </label>
            )}
            <input
              type="text"
              className={`form-control checklist-form-control component-details-input ${hasError ? "is-invalid" : ""}`}
              placeholder="Enter text"
              value={value || ""}
              title={value || ""}
              style={{ minHeight: "47px", maxHeight: "47px", height: "47px" }}
              readOnly
            />
            
          </div>
        </div>
      );
    }

    // Number (QT008) – read-only numeric field display
    if (typeCode === "QT008") {
      const qt008ColClass =
        options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
            ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
            : "col-12 col-md-6 dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt008ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
            <label className={labelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control checklist-form-control component-details-input"
              value={value || ""}
              title={value || ""}
              style={{ minHeight: "47px", maxHeight: "47px", height: "47px" }}
              readOnly
            />
          </div>
        </div>
      );
    }

    // Image Upload (QT009) – render saved response path(s) as image tiles.
    if (typeCode === "QT009") {
      const qt009ColClass =
        options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : "col-12 col-md-12 dynamic-q-row dynamic-q-product-details";
      const imagePaths = parseQt009AnswerPaths(value);
      const imageUrls = imagePaths.map(toFullUrl).filter(Boolean);

      return (
        <div key={code} className={qt009ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
            {!options.optionRowTypedFollowUp && !options.suppressQtFieldLabel && (
              <label className={labelClass} style={labelStyle}>
                {text}
                {mandatory && <span className="text-danger ms-1">*</span>}
              </label>
            )}
            {imageUrls.length === 0 ? (
              <input
                type="text"
                className="form-control checklist-form-control component-details-input"
                value={value || ""}
                readOnly
              />
            ) : (
              <UploadedFileList
                files={imageUrls.map((imgUrl, idx) => ({
                  id: `${code}-img-${idx}`,
                  name: decodeURIComponent(
                    (imagePaths[idx] || "").split("/").pop() || `image-${idx + 1}`
                  ),
                  url: imgUrl,
                  fileType: "image/jpeg",
                }))}
                onOpen={(fileItem) => {
                  if (fileItem.url) window.open(fileItem.url, "_blank");
                }}
              />
            )}
          </div>
        </div>
      );
    }

    // Datepicker (QT010) – read-only date input.
    if (typeCode === "QT010") {
      const qt010ColClass = "dynamic-q-option-item dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt010ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control dynamic-q-control-inline-fit dynamic-q-control-date-inline">
            <label className={labelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <input
              type="date"
              className="form-control checklist-form-control component-details-input"
              value={value || ""}
              readOnly
              disabled
            />
          </div>
        </div>
      );
    }

    // Fallback: text input – label above
    const fallbackColClass =
      options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
        ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
        : "col-12 col-md-12 dynamic-q-row dynamic-q-product-details";
    return (
      <div key={code} className={fallbackColClass} style={indentStyle}>
        <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
        <div className="dynamic-q-control">
          <label className={labelClass} style={labelStyle}>
            {text}
            {mandatory && <span className="text-danger ms-1">*</span>}
          </label>
          <input
            type="text"
            className="form-control checklist-form-control component-details-input"
            placeholder="Enter value"
            value={value || ""}
            readOnly
          />
        </div>
      </div>
    );
  };

  /** Matches nodes that render the full-width .dynamic-q-section-with-children wrapper (QT006 with nested content). */
  const nodeRendersDynamicQSectionWithChildren = (n) => {
    if (!n) return false;
    const t = (n.questionTypeCode || n.QuestionTypeCode || "").toUpperCase();
    if (t !== "QT006") return false;
    const kids = n.children || [];
    if (kids.some((c) => (c.children || []).length > 0)) return true;
    return kids.length > 0;
  };
  /** Lone QT005/QT007 row (no nested sub-question rows under this node). */
  const isQt005OrQt007SingleLeafRow = (n) => {
    if (!n) return false;
    const t = (n.questionTypeCode || n.QuestionTypeCode || "").toUpperCase();
    if (t !== "QT005" && t !== "QT007") return false;
    return !(n.children || []).some((c) => (c.children || []).length > 0);
  };

  /** Per-child index: true when this QT004 is in a gap of 1–2 consecutive QT004 nodes between two QT006 .dynamic-q-section-with-children siblings. */
  const getQt004SandwichedHalfWidthFlags = (arr) => {
    const len = arr.length;
    const out = new Array(len).fill(false);
    if (!len) return out;
    let a = 0;
    while (a < len) {
      while (a < len && !nodeRendersDynamicQSectionWithChildren(arr[a])) a += 1;
      if (a >= len) break;
      let b = a + 1;
      while (b < len && !nodeRendersDynamicQSectionWithChildren(arr[b])) b += 1;
      if (b >= len) break;
      const gap = b - a - 1;
      if (gap >= 1 && gap <= 2) {
        let allQt004 = true;
        for (let k = a + 1; k < b; k += 1) {
          const t = (arr[k].questionTypeCode || arr[k].QuestionTypeCode || "").toUpperCase();
          if (t !== "QT004") {
            allQt004 = false;
            break;
          }
        }
        if (allQt004) {
          for (let k = a + 1; k < b; k += 1) out[k] = true;
        }
      }
      a = b;
    }
    return out;
  };

  const renderQuestionNode = (node, level = 0, options = {}) => {
    const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
    const hasSubQuestionChildren = (node.children || []).some((c) => (c.children || []).length > 0);
    const isQt006Section = typeCode === "QT006" && hasSubQuestionChildren;

    const childrenToRecurse = isQt006Section
      ? (node.children || [])
      : isChecklistOptionBasedParentType(typeCode) ||
          typeCode === "QT013" ||
          typeCode === "QT014" ||
          typeCode === "QT015"
        ? []
        : (node.children || []).filter((child) => (child.children || []).length > 0);

    const soleQt005OrQt007InSectionChildren =
      isQt006Section &&
      childrenToRecurse.length === 1 &&
      (() => {
        const onlyChild = childrenToRecurse[0];
        const t = (onlyChild.questionTypeCode || onlyChild.QuestionTypeCode || "").toUpperCase();
        if (t !== "QT005" && t !== "QT007") return false;
        const hasNestedSubRows = (onlyChild.children || []).some(
          (c) => (c.children || []).length > 0
        );
        return !hasNestedSubRows;
      })();

    const lastIdx = childrenToRecurse.length - 1;
    const lastNode = lastIdx >= 0 ? childrenToRecurse[lastIdx] : null;
    const lastTextFieldFullWidthAfterNestedSection =
      isQt006Section &&
      lastIdx >= 1 &&
      isQt005OrQt007SingleLeafRow(lastNode) &&
      childrenToRecurse.slice(0, lastIdx).some((c) => nodeRendersDynamicQSectionWithChildren(c));
    const lastDropdownFullWidthAfterNestedSection =
      isQt006Section &&
      lastIdx >= 1 &&
      String(lastNode?.questionTypeCode || lastNode?.QuestionTypeCode || "").toUpperCase() === "QT004" &&
      childrenToRecurse.slice(0, lastIdx).some((c) => nodeRendersDynamicQSectionWithChildren(c));

    const exactlyTwoSectionProductDetailRows =
      isQt006Section &&
      childrenToRecurse.length === 2 &&
      childrenToRecurse.every((c) => !nodeRendersDynamicQSectionWithChildren(c));

    const qt004SandwichedHalfWidthFlags = isQt006Section
      ? getQt004SandwichedHalfWidthFlags(childrenToRecurse)
      : null;

    const optionsForChildRecursion =
      !isQt006Section && options && options.qt004HalfWidthBetweenSectionSiblings
        ? (() => {
            const { qt004HalfWidthBetweenSectionSiblings: _omitQt004Root, ...rest } = options;
            return rest;
          })()
        : options;

    const childRows = childrenToRecurse.flatMap((child, idx) =>
      renderQuestionNode(
        child,
        level + 1,
        isQt006Section
          ? {
              parentIsQt006: true,
              siblingIndex: idx,
              inDynamicQSectionChildren: true,
              ...(soleQt005OrQt007InSectionChildren
                ? { soleQt005OrQt007InSectionChildren: true }
                : {}),
              ...(lastTextFieldFullWidthAfterNestedSection && idx === lastIdx
                ? { lastTextFieldFullWidthAfterNestedSection: true }
                : {}),
              ...(lastDropdownFullWidthAfterNestedSection && idx === lastIdx
                ? { lastDropdownFullWidthAfterNestedSection: true }
                : {}),
              ...(exactlyTwoSectionProductDetailRows
                ? { exactlyTwoSectionProductDetailRows: true }
                : {}),
              ...(qt004SandwichedHalfWidthFlags && qt004SandwichedHalfWidthFlags[idx]
                ? { qt004HalfWidthBetweenSectionSiblings: true }
                : {}),
              ...(childrenToRecurse.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
            }
          : optionsForChildRecursion
      )
    );

    if (isQt006Section && childRows.length > 0) {
      const code = node.questionCode || node.QuestionCode || "section";
      const text = node.questionText || node.QuestionText || "";
      const mandatoryRaw = node.isMandatory ?? node.IsMandatory;
      const mandatory =
        mandatoryRaw !== null &&
        mandatoryRaw !== undefined &&
        mandatoryRaw !== false &&
        mandatoryRaw !== 0 &&
        mandatoryRaw !== "0";
      const labelClass = `form-label checklist-form-label component-details-label ${mandatory ? "required" : ""}`;
      const labelStyle = { textAlign: "left", fontWeight: "bold", fontSize: "14px" };
      const qt006ToggleLabelStyle = { ...labelStyle, fontSize: "calc(1em + 1px)" };
      const typeIcon = getDynamicQuestionTypeIcon(typeCode);
      
      // Initialize as expanded (initially open)
      // If code is in collapsedSections Set, it means it was explicitly collapsed
      const isCollapsed = collapsedSections.has(code);
      const shouldBeExpanded = !isCollapsed;
      
      const toggleSection = (e) => {
        e.stopPropagation(); // Prevent any parent click handlers
        setCollapsedSections((prev) => {
          const next = new Set(prev);
          if (next.has(code)) {
            // Currently collapsed, remove from Set to expand
            next.delete(code);
          } else {
            // Currently expanded (not in Set), add to Set to collapse
            next.add(code);
          }
          return next;
        });
      };
      
      return [
        <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children">
          <div 
            className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
            onClick={toggleSection}
            style={{ cursor: "pointer" }}
          >
            <label className={labelClass} style={{ ...qt006ToggleLabelStyle, flex: 1 }}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <span className="dynamic-q-section-caret">
              {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </span>
          </div>
          {shouldBeExpanded && (
            <div className="dynamic-q-section-children">
              <div className="row g-3">{childRows}</div>
            </div>
          )}
        </div>,
      ];
    }

    const rows = [renderDynamicQuestion(node, level, options)];
    return [...rows, ...childRows];
  };

  return { renderQuestionNode, getQt004SandwichedHalfWidthFlags };
}

export default function DynamicChecklistReadOnly({
  templateCode,
  answers,
  fallbackQuestions = [],
  srCategoryFilter = null,
  hideWhenEmpty = false,
  flatQuestionsOverride = null,
}) {
  const checklistLayoutRef = useRef(null);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [flatQuestions, setFlatQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const { renderQuestionNode, getQt004SandwichedHalfWidthFlags } = useMemo(
    () => createChecklistRenderers(answers, collapsedSections, setCollapsedSections),
    [answers, collapsedSections]
  );

  useEffect(() => {
    const tc = String(templateCode || "").trim();
    if (!tc) {
      setFlatQuestions(null);
      setFailed(true);
      return;
    }
    if (Array.isArray(flatQuestionsOverride)) {
      setFlatQuestions(flatQuestionsOverride);
      setFailed(flatQuestionsOverride.length === 0);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    checklistQuestionAPI
      .getQuestionsHierarchy(tc)
      .then((res) => {
        const raw = Array.isArray(res)
          ? res
          : (
              res?.data ??
              res?.data?.data ??
              res?.data?.Data ??
              res?.data?.items ??
              res?.Data ??
              []
            );
        const list = Array.isArray(raw) ? raw : [];
        if (!cancelled) {
          setFlatQuestions(list);
          if (!list.length) setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFlatQuestions(null);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateCode, flatQuestionsOverride]);

  const questionTree = useMemo(() => buildChecklistTree(flatQuestions || []), [flatQuestions]);

  const displayTree = useMemo(() => {
    if (!srCategoryFilter) return questionTree;
    if (srCategoryFilter === "general") return filterGeneralQuestionTree(questionTree);
    return filterQuestionTreeBySrCategoryExcludingGeneral(questionTree, srCategoryFilter);
  }, [questionTree, srCategoryFilter]);

  useEffect(() => {
    if (loading || failed || !questionTree.length) return undefined;

    /** Extra space required before moving checkbox options back beside the label (prevents flicker). */
    const CHECKBOX_RELEASE_EXTRA_SPACE_PX = 48;
    /** Safety margin when deciding to move checkbox options below the label. */
    const CHECKBOX_APPLY_BREAK_GAP_PX = 16;

    let resizeDebounceId = 0;
    let layoutPassScheduled = false;

    const applyInlineAutoFit = () => {
      const getNaturalOptionsWidth = (optionsEl) => {
        if (!(optionsEl instanceof HTMLElement)) return 0;
        const inner = optionsEl.firstElementChild;
        if (!(inner instanceof HTMLElement)) {
          return Math.ceil(optionsEl.scrollWidth || optionsEl.getBoundingClientRect().width || 0);
        }
        const items = Array.from(inner.children).filter((el) => el instanceof HTMLElement);
        if (items.length === 0) {
          return Math.ceil(inner.scrollWidth || inner.getBoundingClientRect().width || 0);
        }
        const innerStyles = window.getComputedStyle(inner);
        const gap =
          parseFloat(innerStyles.columnGap || innerStyles.gap || "0") ||
          parseFloat(innerStyles.getPropertyValue("row-gap") || "0") ||
          0;
        const itemsWidth = items.reduce(
          (sum, item) => sum + Math.ceil(item.getBoundingClientRect().width || 0),
          0
        );
        return Math.ceil(itemsWidth + gap * Math.max(items.length - 1, 0));
      };

      const getAvailableControlWidth = (controlEl) => {
        const parentContainer =
          controlEl.closest(".dynamic-q-option-item-full") ||
          controlEl.closest(".dynamic-q-option-item") ||
          controlEl.closest(".dynamic-q-section-children") ||
          controlEl.closest(".dynamic-q-section-with-children") ||
          controlEl.closest(".qtnstrvw") ||
          controlEl.closest(".checklist-form");
        const rowEl = controlEl.closest(".dynamic-q-row");
        const rowGap =
          rowEl instanceof HTMLElement
            ? parseFloat(window.getComputedStyle(rowEl).gap || "0") || 0
            : 0;
        const iconEl =
          rowEl instanceof HTMLElement
            ? rowEl.querySelector(":scope > .dynamic-q-icon")
            : null;
        const iconWidth =
          iconEl instanceof HTMLElement &&
          window.getComputedStyle(iconEl).display !== "none"
            ? Math.ceil(iconEl.getBoundingClientRect().width || 0) + rowGap
            : 0;
        const containerWidth =
          (parentContainer instanceof HTMLElement ? parentContainer.clientWidth : 0) ||
          (rowEl instanceof HTMLElement ? rowEl.clientWidth : 0) ||
          controlEl.clientWidth ||
          0;
        return Math.max(0, containerWidth - iconWidth);
      };

      const root = checklistLayoutRef.current;
      const controls = root
        ? root.querySelectorAll(".dynamic-q-control.dynamic-q-control-inline-auto")
        : document.querySelectorAll(
            ".raise-page .dynamic-q-control.dynamic-q-control-inline-auto"
          );
      controls.forEach((controlEl) => {
        const labelEl = controlEl.querySelector(":scope > .component-details-label");
        const optionsEl = controlEl.querySelector(":scope > .dynamic-q-inline-options");
        if (!(labelEl instanceof HTMLElement) || !(optionsEl instanceof HTMLElement)) return;

        const availableWidth = getAvailableControlWidth(controlEl);
        if (!availableWidth) return;

        const labelWidth = Math.ceil(labelEl.getBoundingClientRect().width || 0);
        const optionsOneLineWidth = getNaturalOptionsWidth(optionsEl);
        const gap = parseFloat(window.getComputedStyle(controlEl).columnGap || "0") || 0;
        const isCheckboxOptions = optionsEl.classList.contains("dynamic-q-checkbox-options");
        const checkboxCount = isCheckboxOptions
          ? optionsEl.querySelectorAll(".form-check").length
          : 0;
        const requiredInlineWidth = labelWidth + gap + optionsOneLineWidth;
        const currentlyBroken = controlEl.classList.contains("dynamic-q-control-inline-break");
        let needsBreak;
        if (isCheckboxOptions) {
          if (checkboxCount > 4) {
            needsBreak = true;
          } else if (currentlyBroken) {
            needsBreak =
              requiredInlineWidth > availableWidth - CHECKBOX_RELEASE_EXTRA_SPACE_PX;
          } else {
            needsBreak =
              requiredInlineWidth + CHECKBOX_APPLY_BREAK_GAP_PX > availableWidth;
          }
        } else {
          needsBreak = requiredInlineWidth > availableWidth;
        }

        controlEl.classList.toggle("dynamic-q-control-inline-break", needsBreak);
      });
    };

    const runInlineAutoFit = () => {
      applyInlineAutoFit();
      window.requestAnimationFrame(applyInlineAutoFit);
    };

    const scheduleInlineAutoFit = () => {
      if (layoutPassScheduled) return;
      layoutPassScheduled = true;
      window.requestAnimationFrame(() => {
        layoutPassScheduled = false;
        runInlineAutoFit();
      });
    };

    const scheduleInlineAutoFitDebounced = () => {
      window.clearTimeout(resizeDebounceId);
      resizeDebounceId = window.setTimeout(scheduleInlineAutoFit, 120);
    };

    runInlineAutoFit();
    const loadRetryDelaysMs = [0, 100, 350, 800];
    const timeoutIds = loadRetryDelaysMs.map((delayMs) =>
      window.setTimeout(runInlineAutoFit, delayMs)
    );

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => runInlineAutoFit()).catch(() => {});
    }

    window.addEventListener("resize", scheduleInlineAutoFitDebounced);
    window.addEventListener("load", runInlineAutoFit);

    const handleCollapseShown = (event) => {
      const target = event.target;
      const layoutRoot = checklistLayoutRef.current;
      if (
        !(target instanceof HTMLElement) ||
        !layoutRoot ||
        !target.contains(layoutRoot)
      ) {
        return;
      }
      runInlineAutoFit();
      window.setTimeout(runInlineAutoFit, 200);
    };
    document.addEventListener("shown.bs.collapse", handleCollapseShown);

    let resizeObserver;
    const layoutRoot = checklistLayoutRef.current;
    if (layoutRoot && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => scheduleInlineAutoFitDebounced());
      resizeObserver.observe(layoutRoot);
    }

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(resizeDebounceId);
      window.removeEventListener("resize", scheduleInlineAutoFitDebounced);
      window.removeEventListener("load", runInlineAutoFit);
      document.removeEventListener("shown.bs.collapse", handleCollapseShown);
      resizeObserver?.disconnect();
    };
  }, [loading, failed, displayTree, answers, collapsedSections]);

  if (loading) {
    return <p className="text-muted small mb-0 py-2">Loading checklist layout…</p>;
  }
  if (hideWhenEmpty && !failed && displayTree.length === 0) {
    return null;
  }
  if (failed || !displayTree.length) {
    if (hideWhenEmpty) return null;
    return <ChecklistFlatFallback questions={fallbackQuestions} />;
  }

  const rootQt004SandwichFlags = getQt004SandwichedHalfWidthFlags(displayTree);
  return (
    <div className="raise-page" ref={checklistLayoutRef}>
      <div className="checklist-form raise-complaint-form dynamic-checklist-wrapper pt-0">
        <div
          className={`row g-3 mb-3 mt-2 qtnstrvw${
            srCategoryFilter === "general" ? " checklist-general-questions" : ""
          }${srCategoryFilter && srCategoryFilter !== "general" ? " checklist-category-questions" : ""}`}
        >
          {displayTree.flatMap((node, idx) =>
            renderQuestionNode(node, 0, {
              ...(rootQt004SandwichFlags[idx] ? { qt004HalfWidthBetweenSectionSiblings: true } : {}),
            })
          )}
        </div>
      </div>
    </div>
  );
}
